import React, { useState, useCallback, useMemo, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FieldError, svgSprite } from '../../../../../hybrid/wrappers';
import SearchWindow from '../../../../searchWindow/SearchWindow';
import UserInfoBlock from '../UserInfoBlock/UserInfoBlock';
import { API_SERVER_URL, useWidgetTranslates } from '../../../../../common/utils/utils';
import UserContext, { UserProfileContext } from '../../../../../common/utils/getContext';
import FromInput from '../../form-input/FormInput/FormInput';
import { useWindowDimensions } from '../../../../../common/utils/windowDimensions';
import { t } from '../../../../../common/components/T';
import { getData } from '../../../../../common/utils/router';
import CircleLoader from '../../../../plug/CircleLoader';
import { getDefaultVariation } from '../../helpers';
import css from './SelectDataWidget.module.scss';
import classnames from 'classnames';
import { generateCompStylers } from '../../../../../common/utils/styles';

const Wrapper = (props) => {
    if (props?.helpButtons) return <div {...props}>{props.children}</div>;
    return <>{props.children}</>;
};
const AdminFormSponsorWidget = (props) => {
    const {
        attribute,
        handlers = {},
        compStyle,
        value,
        setFormData,
        customData = {},
        variation,
        title,
        placeholder,
        disabled,
        notDisabled,
        fastSearch = false,
        customFatherComponent,
        errors
    } = props;
    const { userToken, setAlertData, lang } = useContext(UserContext);
    const profileContext = useContext(UserProfileContext) || {};
    const variationStyle = variation || getDefaultVariation(profileContext, title);
    const [val, setVal] = useState(value);
    const [showSponsor, setShowSponsor] = useState(false);
    const widthDimensions = useWindowDimensions();
    const screenWidth = useMemo(() => widthDimensions <= 768, [widthDimensions]);
    const getUserData = useCallback(
        async (data) => {
            const newData =
                typeof data?.username == 'object'
                    ? {
                          ...data,
                          username: data?.username?.username
                      }
                    : data;
            setAlertData(false);
            if (customData?.transformResultData) {
                setFormData(attribute, customData?.transformResultData(newData));
            } else {
                setFormData(attribute, newData);
            }
            if (!customData?.url && !customData?.hideUserInfo) {
                setVal({ newData, id: data?.id });
            } else {
                setVal(customData?.transformResultData ? customData?.transformResultData(newData) : newData);
            }
            if (handlers?.onBlur) {
                handlers.onBlur(attribute, newData);
            }
            setShowSponsor(false);
        },
        [setAlertData, customData, handlers, setFormData, attribute]
    );
    useEffect(() => {
        if (value?.id && !customData?.url) {
            (async () => {
                const userInfo = await getData(`/api/v1/user/partners/${value.id}`, userToken, lang);
                if (userInfo && userInfo?.data) {
                    setVal(userInfo.data);
                }
            })();
        }
    }, [customData?.url, lang, userToken, value?.id]);
    useEffect(() => {
        setVal(value);
    }, [value]);
    useEffect(() => {
        if (value?.id && customData?.loading === false && customData?.url) {
            setVal(value);
        }
    }, [customData, value]);
    const transl = useWidgetTranslates('form');
    const { stylers, classes } = generateCompStylers(compStyle);

    return (
        <>
            <div
                style={stylers.wrapper}
                className={classnames(
                    css.formGroup,
                    css[variationStyle],
                    css[`${variationStyle}--formSelectDataWrap`],
                    classes.wrapper
                )}
            >
                {customFatherComponent && React.isValidElement(customFatherComponent) ? (
                    <>
                        <div style={{ width: 'fit-content' }} onClick={disabled ? () => {} : () => setShowSponsor(true)}>
                            {customFatherComponent}
                        </div>
                        {errors && errors.length
                            ? errors.map((err, idx) => (
                                  <FieldError compStyle={compStyle?.error} key={`${attribute}${idx}`}>
                                      {err}
                                  </FieldError>
                              ))
                            : null}
                    </>
                ) : (
                    <Wrapper helpButtons={customData?.helpButtons} className={classnames(css.formSelectDataButtonsInputWrap)}>
                        <FromInput
                            {...props}
                            placeholder={customData?.loading ? <CircleLoader /> : placeholder}
                            handlers={{
                                ...(props.handlers || {}),
                                onClick: disabled ? () => {} : () => setShowSponsor(true)
                            }}
                            buttons={{
                                icon: { icon: customData?.icon || 'user-gt-2' },
                                ...(props.buttons || {})
                            }}
                            noWrapper={true}
                            value={val && val[customData.attrToInputName] ? val[customData.attrToInputName] : val?.username || ''}
                            readonly={true}
                            disabled={notDisabled ? false : disabled || Boolean(val) ? true : false}
                            compStyle={{
                                ...compStyle,
                                placeholderWrapper: { cursor: 'inherit' },
                                input: {
                                    ...stylers.input,
                                    className: classnames(customData?.helpButtons ? css.formSelectDataInput : '', classes.input)
                                },
                                title: {
                                    ...stylers.title,
                                    className: classnames(
                                        customData?.helpButtons && variationStyle !== 'couple_title'
                                            ? css.formSelectDataTitle
                                            : '',
                                        classes.title
                                    )
                                }
                            }}
                        />
                        {customData?.helpButtons ? (
                            <div className={css.formSelectDataButtonsWrap}>
                                {val ? (
                                    <>
                                        <div
                                            onClick={() => setShowSponsor(true)}
                                            style={stylers.helpBtn}
                                            className={classnames(css.formSelectDataHelpBtn, classes.helpBtn)}
                                        >
                                            {svgSprite('pen-field', {
                                                width: '24px',
                                                height: '24px',
                                                className: css.formSelectDataHelpBtnSvg
                                            })}
                                        </div>
                                        <div
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                setVal(null);
                                                setFormData(attribute, null);
                                            }}
                                            style={stylers.helpBtn}
                                            className={classnames(css.formSelectDataHelpBtn, classes.helpBtn)}
                                        >
                                            {svgSprite('trash', {
                                                width: '22px',
                                                height: '22px',
                                                className: classnames(
                                                    css.formSelectDataHelpBtnSvg,
                                                    css.formSelectDataHelpBtnSvgDanger
                                                )
                                            })}
                                        </div>
                                    </>
                                ) : null}
                            </div>
                        ) : null}
                    </Wrapper>
                )}
                {!!val && !customData?.hideUserInfo ? <UserInfoBlock data={val} translation={transl} /> : null}
            </div>
            {showSponsor && (
                <SearchWindow
                    input={customData?.modalInput || {}}
                    transformSearchData={customData?.transformSearchData || null}
                    thead={customData?.thead || null}
                    portalClass={!screenWidth ? 'modal' : 'modalBottom'}
                    closeModal={() => setShowSponsor(false)}
                    sendData={{
                        url: customData?.url || `${API_SERVER_URL}/api/v1/admin/user-management/sponsors`
                    }}
                    title={customData?.modalTitle || t('Form-ChooseSponsor', 'Выбрать спонсора', 'widget', transl, 'form')}
                    customData={{ ...customData, buttonNotShow: true }}
                    gridHandlers={{
                        tr: {
                            onClick: async (rowData) => {
                                await getUserData(rowData);
                            }
                        }
                    }}
                    customStyle={{
                        ...compStyle,
                        searchWindowWrapper: !screenWidth ? { width: '1184px' } : {}
                    }}
                    fastSearch={fastSearch}
                />
            )}
        </>
    );
};
AdminFormSponsorWidget.propTypes = {
    attribute: PropTypes.string,
    handlers: PropTypes.object,
    compStyle: PropTypes.object,
    value: PropTypes.object,
    setFormData: PropTypes.func,
    customData: PropTypes.object,
    variation: PropTypes.string,
    title: PropTypes.object,
    placeholder: PropTypes.node
};
export default AdminFormSponsorWidget;
